import type { ReactNode } from 'react';

export type Source = 'measured' | 'real' | 'simulated';

/**
 * Marks simulated data. Anything whose provenance is `simulated` must render this so it
 * never looks identical to a measured value.
 */
export function SimulatedTag({ title = 'Simulated data, not measured' }: { title?: string }) {
  return (
    <span className="hv-sim" title={title} aria-label={title}>
      Sim
    </span>
  );
}

/** A value with its provenance: simulated values get a dotted underline and a tag. */
export function Value({ children, source = 'measured' }: { children: ReactNode; source?: Source }) {
  if (source !== 'simulated') return <>{children}</>;
  return (
    <>
      <span className="hv-value--simulated">{children}</span>
      <SimulatedTag />
    </>
  );
}

/** Keeper-style KPI tile: round icon, big value, caption. */
export function KpiTile({
  icon,
  value,
  label,
  tone = 'blue',
  source,
}: {
  icon: ReactNode;
  value: ReactNode;
  label: ReactNode;
  tone?: 'blue' | 'teal' | 'red';
  source?: Source;
}) {
  return (
    <div className="hv-kpi">
      <span aria-hidden="true" className={`hv-kpi__icon${tone === 'blue' ? '' : ` hv-kpi__icon--${tone}`}`}>
        {icon}
      </span>
      <div className="hv-kpi__body">
        <span className="hv-kpi__value">
          <Value source={source}>{value}</Value>
        </span>
        <span className="hv-kpi__label">{label}</span>
      </div>
    </div>
  );
}

/** Master-control stat: uppercase label over a value with an optional unit. */
export function Stat({
  label,
  value,
  unit,
  source,
  bordered,
}: {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  source?: Source;
  bordered?: boolean;
}) {
  return (
    <div className={`hv-stat${bordered ? ' hv-stat--bordered' : ''}`}>
      <span className="hv-stat__label">{label}</span>
      <span className="hv-stat__value">
        <Value source={source}>
          {value}
          {unit !== undefined && <span className="hv-stat__unit">{unit}</span>}
        </Value>
      </span>
    </div>
  );
}

export function KeyValue({ label, children, source }: { label: ReactNode; children: ReactNode; source?: Source }) {
  return (
    <div className="hv-kv">
      <span className="hv-kv__key">{label}</span>
      <span className="hv-kv__value">
        <Value source={source}>{children}</Value>
      </span>
    </div>
  );
}

export function SectionLabel({ children, dense, as: Tag = 'h2' }: { children: ReactNode; dense?: boolean; as?: 'h2' | 'h3' | 'span' }) {
  return <Tag className={`hv-section-label${dense ? ' hv-section-label--dense' : ''}`}>{children}</Tag>;
}
